import { Text } from "@chakra-ui/react"
import { Link } from "react-router-dom"
import React, { useContext, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { FaRegSun } from "react-icons/fa6"
import { useHistory } from "react-router-dom"
import AuthForm from "src/components/common/authForm"
import FormText from "src/components/common/formText"
import { SDKContext } from "src/context/SDKContext"
import useConfig from "src/hooks/useConfig"
import { useIsMobileLandscape } from "src/util/browser"
import axios from "src/shared/axios"
import Input from "./components/Input"
import Submit from "./components/Submit"
import Heading from "./components/Heading"
import { Logo } from "src/components/header/logo"
import { Routes } from "src/constants"
import usePageStyling from "src/hooks/usePageStyling"
import useAuthedUser from "src/hooks/useAuthedUser"
import useWindowSize from "src/hooks/useWindowSize"
import { addCurrentQueryParams } from "src/util"

type Types = "login" | "register" | "verify" | "password-reset"

export function createHost(url: string) {
  const queryParams = new URLSearchParams(window.location.search)
  const partnerParam = queryParams.get("partner")
  const partnerSubdomain = partnerParam != null ? partnerParam + "." : ""
  return partnerSubdomain + url
}

function Authentication({ type }: { type: Types }) {
  const {
    endUserLogin,
    endUserRegistration,
    learnmachineAuthPage,
    postLoginRedirectUrl
  } = useConfig()
  const history = useHistory()
  const { t } = useTranslation()
  const roomStyles = usePageStyling("authPageStyling")
  const meetingClient = useContext(SDKContext)
  const isMobileLandscape = useIsMobileLandscape()

  const accessDisabled =
    (type === "login" && !endUserLogin) ||
    (type === "register" && !endUserRegistration) ||
    (type === "verify" && !endUserRegistration)
  const configIsLoading =
    endUserLogin === "loading" || endUserRegistration === "loading"

  useEffect(() => {
    if (configIsLoading) {
      return
    }
    if (accessDisabled) {
      history.goBack()
    }
  }, [type, endUserLogin, endUserRegistration])
  if (accessDisabled) return null

  const { getAuthedUser } = useAuthedUser()

  /*
   * This useEffect navigates the user away from the auth page(s) if they are already logged in.
   */
  useEffect(() => {
    if (configIsLoading) return
    const doRedirectIfLoggedIn = async () => {
      const user = await getAuthedUser()
      if (user != null && user.verified) {
        history.replace(postLoginRedirectUrl)
      }
    }
    doRedirectIfLoggedIn()
  }, [configIsLoading])

  const form = (isLearnMachine: boolean) => {
    if (isLearnMachine) {
      switch (type) {
        case "login":
          return (
            <AuthForm
              type="login"
              logo={<FaRegSun color="orange" size="3em" />}
              heading={
                isMobileLandscape ? null : (
                  <Heading.Learnmachine
                    title={t("Sign in")}
                    isMobileLandscape={isMobileLandscape}
                  />
                )
              }
              hasOAuth
              emailInput={({ errors, values, handleChange, handleBlur }) => (
                <Input.Learnmachine
                  title={t("Email")}
                  type="email"
                  id="email-input"
                  name="email"
                  error={errors.email}
                  value={values.email}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  autoComplete="email"
                />
              )}
              passwordInput={({ errors, values, handleChange, handleBlur }) => (
                <Input.Learnmachine
                  title={t("Password")}
                  type="password"
                  id="password-input"
                  name="password"
                  error={errors.password}
                  value={values.password}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  autoComplete="current-password"
                />
              )}
              submitButton={({ isSubmitting }) => (
                <Submit.Learnmachine
                  isSubmitting={isSubmitting}
                  title={t("Sign in")}
                />
              )}
              onSubmitRequest={({ email, password }) => {
                if (!meetingClient) throw "Meeting client not ready"
                return axios.post(
                  meetingClient?.envConfig.USER_LOGIN,
                  {
                    email,
                    password,
                    host: createHost(meetingClient?.envConfig.DOMAIN_HOSTNAME)
                  },
                  {
                    withCredentials: true,
                    headers: {
                      "Content-Type": "application/json"
                    }
                  }
                )
              }}
              redirectToUrl={postLoginRedirectUrl as string}
              navigationPanel={
                <div
                  style={{
                    display: "flex",
                    justifyContent: isMobileLandscape
                      ? "space-evenly"
                      : "center",
                    flexDirection: isMobileLandscape ? "row" : "column"
                  }}
                >
                  <FormText textAlign="center">
                    <>
                      {t("Need an account?")}{" "}
                      <span style={{ fontWeight: "bold" }}>
                        <Link to={addCurrentQueryParams("/user-register")}>
                          {t("Sign up")}
                        </Link>
                      </span>
                    </>
                  </FormText>
                  <FormText textAlign="center">
                    <>
                      {t("Forgot your password?")}{" "}
                      <span style={{ fontWeight: "bold" }}>
                        <Link
                          to={addCurrentQueryParams("/user-password-reset")}
                        >
                          {t("Reset it")}
                        </Link>
                      </span>
                    </>
                  </FormText>
                </div>
              }
            />
          )

        case "register":
          return (
            <AuthForm
              type="register"
              logo={<FaRegSun color="orange" size="3em" />}
              heading={
                isMobileLandscape ? null : (
                  <Heading.Learnmachine
                    title={t("Sign up")}
                    isMobileLandscape={isMobileLandscape}
                  />
                )
              }
              hasOAuth
              emailInput={({ errors, values, handleChange, handleBlur }) => (
                <Input.Learnmachine
                  title={t("Email")}
                  type="email"
                  id="email-input"
                  name="email"
                  error={errors.email}
                  value={values.email}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  autoComplete="email"
                />
              )}
              passwordInput={({ errors, values, handleChange, handleBlur }) => (
                <Input.Learnmachine
                  title={t("Password")}
                  type="password"
                  id="password-input"
                  name="password"
                  error={errors.password}
                  value={values.password}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  autoComplete="new-password"
                />
              )}
              passwordConfirmInput={({
                errors,
                values,
                handleChange,
                handleBlur
              }) => (
                <Input.Learnmachine
                  title={t("Confirm Password")}
                  type="password"
                  id="passwordConfirm-input"
                  name="passwordConfirm"
                  error={errors.passwordConfirm}
                  value={values.passwordConfirm}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  autoComplete="new-password"
                />
              )}
              submitButton={({ isSubmitting }) => (
                <Submit.Learnmachine
                  isSubmitting={isSubmitting}
                  title={t("Sign up")}
                />
              )}
              onSubmitRequest={({
                email,
                password,
                passwordConfirm,
                recaptchaToken
              }) => {
                if (!meetingClient) throw "Meeting client not ready"
                return axios.post(
                  meetingClient?.envConfig.USER_REGISTRATION,
                  {
                    email,
                    password,
                    passwordConfirm,
                    host: createHost(meetingClient?.envConfig.DOMAIN_HOSTNAME),
                    recaptchaToken
                  },
                  {
                    withCredentials: true,
                    headers: {
                      "Content-Type": "application/json"
                    }
                  }
                )
              }}
              redirectToUrl={postLoginRedirectUrl as string}
              navigationPanel={
                <div
                  style={{
                    display: "flex",
                    flexDirection: isMobileLandscape ? "row" : "column",
                    justifyContent: isMobileLandscape
                      ? "space-evenly"
                      : "center"
                  }}
                >
                  <FormText>
                    <p style={{ textAlign: "center" }}>
                      {t("Already got an account?")}{" "}
                      <span style={{ fontWeight: "bold" }}>
                        <Link to={addCurrentQueryParams("/user-login")}>
                          {t("Sign in")}
                        </Link>
                      </span>
                    </p>
                  </FormText>
                </div>
              }
            />
          )

        case "verify":
          return (
            <AuthForm
              type="verify"
              logo={<FaRegSun color="orange" size="3em" />}
              heading={
                isMobileLandscape ? null : (
                  <Heading.Learnmachine
                    title={t("Email Verification")}
                    isMobileLandscape={isMobileLandscape}
                  />
                )
              }
              onSubmitRequest={({ verifyCode }) => {
                if (!meetingClient) throw "Meeting client not ready"
                return axios.post(
                  meetingClient?.envConfig.USER_VERIFY,
                  {
                    verifyCode,
                    host: createHost(meetingClient?.envConfig.DOMAIN_HOSTNAME)
                  },
                  {
                    withCredentials: true,
                    headers: {
                      "Content-Type": "application/json"
                    }
                  }
                )
              }}
              redirectToUrl={postLoginRedirectUrl as string}
              instructionText={
                <>
                  <Text textAlign="center" color="white" paddingBottom="10px">
                    {t(
                      "You must verify your email. Check your inbox for a link."
                    )}
                  </Text>
                </>
              }
              codeInput={({ errors, values, handleChange, handleBlur }) => (
                <Input.Learnmachine
                  title={t("Email code")}
                  name="verifyCode"
                  id="emailCode-input"
                  type="tel"
                  error={errors.verifyCode}
                  value={values.verifyCode}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  autoComplete="one-time-code"
                />
              )}
              submitButton={({ isSubmitting }) => (
                <Submit.Learnmachine
                  isSubmitting={isSubmitting}
                  title={t("Submit")}
                />
              )}
              navigationPanel={
                <div
                  style={{
                    display: "flex",
                    flexDirection: isMobileLandscape ? "row" : "column",
                    justifyContent: isMobileLandscape
                      ? "space-evenly"
                      : "center"
                  }}
                >
                  <FormText>
                    <>
                      {t("Want to try another account?")}{" "}
                      <span style={{ fontWeight: "bold" }}>
                        <span
                          style={{ cursor: "pointer" }}
                          onClick={async () => {
                            if (!meetingClient) throw "Meeting client not ready"
                            const res = await axios.post(
                              meetingClient?.envConfig.USER_LOGOUT,
                              {},
                              {
                                withCredentials: true,
                                headers: {
                                  "Content-Type": "application/json"
                                }
                              }
                            )
                            if (res.status == 200) {
                              history.replace(Routes.LOGIN)
                            }
                          }}
                        >
                          {t("Log out")}
                        </span>
                      </span>
                    </>
                  </FormText>
                </div>
              }
            />
          )

        case "password-reset":
          const queryParams = new URLSearchParams(window.location.search)
          const passwordResetCode = queryParams.get("passwordResetCode")

          return (
            <AuthForm
              type="reset"
              logo={<FaRegSun color="orange" size="3em" />}
              heading={
                isMobileLandscape ? null : (
                  <Heading.Learnmachine
                    title={t("Reset Password")}
                    isMobileLandscape={isMobileLandscape}
                  />
                )
              }
              redirectToUrl={
                passwordResetCode ? (postLoginRedirectUrl as string) : undefined
              }
              successMessage={email => {
                return !passwordResetCode ? (
                  <FormText
                    color={"white"}
                    fontSize="13px"
                    lineHeight="1rem"
                    mt=".75rem"
                    textAlign="center"
                  >
                    {`If ${email} is registered, a password reset email will be sent.`}
                  </FormText>
                ) : null
              }}
              onSubmitRequest={({ email, passwordConfirm }) => {
                if (!meetingClient) throw "Meeting client not ready"
                return axios.post(
                  meetingClient?.envConfig.USER_PASSWORD_RESET,
                  {
                    email,
                    newPassword: passwordConfirm,
                    ...(passwordResetCode
                      ? { passwordResetCode: passwordResetCode }
                      : {}),
                    host: createHost(meetingClient?.envConfig.DOMAIN_HOSTNAME)
                  },
                  {
                    withCredentials: true,
                    headers: {
                      "Content-Type": "application/json"
                    }
                  }
                )
              }}
              emailInput={({ errors, values, handleChange, handleBlur }) => (
                <Input.Learnmachine
                  title={t("Email")}
                  type="email"
                  name="email"
                  id="email-input"
                  error={errors.email}
                  value={values.email}
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                  autoComplete="email"
                />
              )}
              {...(!passwordResetCode
                ? {}
                : {
                    passwordInput: ({
                      errors,
                      values,
                      handleChange,
                      handleBlur
                    }) => (
                      <Input.Learnmachine
                        title={t("Password")}
                        type="password"
                        name="password"
                        id="password-input"
                        error={errors.password}
                        value={values.password}
                        handleBlur={handleBlur}
                        handleChange={handleChange}
                        autoComplete="new-password"
                      />
                    ),
                    passwordConfirmInput: ({
                      errors,
                      values,
                      handleChange,
                      handleBlur
                    }) => (
                      <Input.Learnmachine
                        title={t("Confirm Password")}
                        type="password"
                        name="passwordConfirm"
                        id="passwordConfirm-input"
                        error={errors.passwordConfirm}
                        value={values.passwordConfirm}
                        handleBlur={handleBlur}
                        handleChange={handleChange}
                        autoComplete="new-password"
                      />
                    )
                  })}
              submitButton={({ isSubmitting }) => (
                <Submit.Learnmachine
                  isSubmitting={isSubmitting}
                  title={t("Reset")}
                />
              )}
              navigationPanel={
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    flexDirection: isMobileLandscape ? "row" : "column",
                    justifyContent: isMobileLandscape
                      ? "space-evenly"
                      : "center"
                  }}
                >
                  <FormText>
                    <>
                      {t("Already got an account?")}{" "}
                      <span style={{ fontWeight: "bold" }}>
                        <Link to={addCurrentQueryParams("/user-login")}>
                          {t("Sign in")}
                        </Link>
                      </span>
                    </>
                  </FormText>
                </div>
              }
            />
          )

        default:
          return <>Unhandled form type</>
      }
    }

    switch (type) {
      case "login":
        return (
          <AuthForm
            type="login"
            heading={
              isMobileLandscape ? null : (
                <Heading.Default title={t("Sign in")} />
              )
            }
            logo={
              isMobileLandscape ? null : (
                <Logo src={roomStyles?.authPageHeaderImage} noPad />
              )
            }
            hasOAuth
            emailInput={({ errors, values, handleChange, handleBlur }) => (
              <Input.Default
                title={t("Email")}
                type="email"
                error={errors.email}
                value={values.email}
                id="email-input"
                name="email"
                handleBlur={handleBlur}
                handleChange={handleChange}
                autoComplete="email"
              />
            )}
            passwordInput={({ errors, values, handleChange, handleBlur }) => (
              <Input.Default
                title={t("Password")}
                type="password"
                error={errors.password}
                value={values.password}
                id="password-input"
                name="password"
                handleBlur={handleBlur}
                handleChange={handleChange}
                autoComplete="current-password"
              />
            )}
            submitButton={({ isSubmitting }) => (
              <Submit.Default
                title={t("Sign in")}
                isSubmitting={isSubmitting}
              />
            )}
            onSubmitRequest={({ email, password }) => {
              if (!meetingClient) throw "Meeting client not ready"
              return axios.post(
                meetingClient?.envConfig.USER_LOGIN,
                {
                  email,
                  password,
                  host: createHost(meetingClient?.envConfig.DOMAIN_HOSTNAME)
                },
                {
                  withCredentials: true,
                  headers: {
                    "Content-Type": "application/json"
                  }
                }
              )
            }}
            redirectToUrl={postLoginRedirectUrl as string}
            navigationPanel={
              <div
                style={{
                  display: "flex",
                  justifyContent: isMobileLandscape ? "space-evenly" : "center",
                  flexDirection: isMobileLandscape ? "row" : "column"
                }}
              >
                <p
                  style={{
                    textAlign: "center",
                    color: roomStyles?.authPagePanelSubTextColor
                      ? roomStyles?.authPagePanelSubTextColor
                      : "#4A5568"
                  }}
                >
                  {t("Need an account?")}{" "}
                  <Link
                    style={{ textDecoration: "underline" }}
                    to={addCurrentQueryParams("/user-register")}
                  >
                    {t("Sign up")}
                  </Link>
                </p>
                <p
                  style={{
                    textAlign: "center",
                    color: roomStyles?.authPagePanelSubTextColor
                      ? roomStyles?.authPagePanelSubTextColor
                      : "#4A5568"
                  }}
                >
                  {t("Forgot your password?")}{" "}
                  <Link
                    style={{ textDecoration: "underline" }}
                    to={addCurrentQueryParams("/user-password-reset")}
                  >
                    {t("Reset it")}
                  </Link>
                </p>
              </div>
            }
          />
        )

      case "register":
        return (
          <AuthForm
            type="register"
            heading={
              isMobileLandscape ? null : (
                <Heading.Default title={t("Sign up")} />
              )
            }
            logo={<Logo src={roomStyles?.authPageHeaderImage} noPad />}
            hasOAuth
            onSubmitRequest={({
              email,
              password,
              passwordConfirm,
              recaptchaToken
            }) => {
              if (!meetingClient) throw "Meeting client not ready"
              return axios.post(
                meetingClient?.envConfig.USER_REGISTRATION,
                {
                  email,
                  password,
                  passwordConfirm,
                  host: createHost(meetingClient?.envConfig.DOMAIN_HOSTNAME),
                  recaptchaToken
                },
                {
                  withCredentials: true,
                  headers: {
                    "Content-Type": "application/json"
                  }
                }
              )
            }}
            redirectToUrl={postLoginRedirectUrl as string}
            emailInput={({ errors, values, handleChange, handleBlur }) => (
              <Input.Default
                title={t("Email")}
                type="email"
                error={errors.email}
                value={values.email}
                id="email-input"
                name="email"
                handleBlur={handleBlur}
                handleChange={handleChange}
                autoComplete="email"
              />
            )}
            passwordInput={({ errors, values, handleChange, handleBlur }) => (
              <Input.Default
                title={t("Password")}
                type="password"
                error={errors.password}
                value={values.password}
                id="password-input"
                name="password"
                handleBlur={handleBlur}
                handleChange={handleChange}
                autoComplete="new-password"
              />
            )}
            passwordConfirmInput={({
              errors,
              values,
              handleChange,
              handleBlur
            }) => (
              <Input.Default
                title={t("Confirm Password")}
                type="password"
                error={errors.passwordConfirm}
                value={values.passwordConfirm}
                id="passwordConfirm-input"
                name="passwordConfirm"
                handleBlur={handleBlur}
                handleChange={handleChange}
                autoComplete="new-password"
              />
            )}
            submitButton={({ isSubmitting }) => (
              <Submit.Default
                title={t("Sign up")}
                isSubmitting={isSubmitting}
              />
            )}
            navigationPanel={
              <div
                style={{
                  display: "flex",
                  justifyContent: isMobileLandscape ? "space-evenly" : "center",
                  flexDirection: isMobileLandscape ? "row" : "column"
                }}
              >
                <p
                  style={{
                    textAlign: "center",
                    color: roomStyles?.authPagePanelSubTextColor
                      ? roomStyles?.authPagePanelSubTextColor
                      : "#4A5568"
                  }}
                >
                  {t("Already got an account?")}{" "}
                  <Link
                    to={addCurrentQueryParams("/user-login")}
                    style={{ textDecoration: "underline" }}
                  >
                    {t("Sign in")}
                  </Link>
                </p>
              </div>
            }
          />
        )

      case "verify":
        return (
          <AuthForm
            type="verify"
            heading={
              isMobileLandscape ? null : (
                <Heading.Default title={t("Email Verification")} />
              )
            }
            logo={<Logo src={roomStyles?.authPageHeaderImage} noPad />}
            onSubmitRequest={({ verifyCode }) => {
              if (!meetingClient) throw "Meeting client not ready"
              return axios.post(
                meetingClient?.envConfig.USER_VERIFY,
                {
                  verifyCode,
                  host: createHost(meetingClient?.envConfig.DOMAIN_HOSTNAME)
                },
                {
                  withCredentials: true,
                  headers: {
                    "Content-Type": "application/json"
                  }
                }
              )
            }}
            redirectToUrl={postLoginRedirectUrl as string}
            instructionText={
              <>
                <Text
                  textAlign={"center"}
                  color={roomStyles?.authPagePanelTextColor}
                  paddingBottom={"10px"}
                >
                  {t(
                    "You must verify your email. Check your inbox for a link."
                  )}
                </Text>
              </>
            }
            codeInput={({ errors, values, handleChange, handleBlur }) => (
              <Input.Default
                error={errors.verifyCode}
                value={values.verifyCode}
                name="verifyCode"
                id="code-input"
                type="tel"
                handleChange={handleChange}
                handleBlur={handleBlur}
                title={t("Email code")}
                autoComplete="one-time-code"
              />
            )}
            submitButton={({ isSubmitting }) => (
              <Submit.Default title={t("Submit")} isSubmitting={isSubmitting} />
            )}
            navigationPanel={
              <div
                style={{
                  display: "flex",
                  justifyContent: isMobileLandscape ? "space-evenly" : "center",
                  flexDirection: isMobileLandscape ? "row" : "column"
                }}
              >
                <p
                  style={{
                    textAlign: "center",
                    color: roomStyles?.authPagePanelSubTextColor
                      ? roomStyles?.authPagePanelSubTextColor
                      : "#4A5568"
                  }}
                >
                  {t("Want to try another account?")}{" "}
                  <span
                    style={{ textDecoration: "underline", cursor: "pointer" }}
                    onClick={async () => {
                      if (!meetingClient) throw "Meeting client not ready"
                      const res = await axios.post(
                        meetingClient?.envConfig.USER_LOGOUT,
                        {},
                        {
                          withCredentials: true,
                          headers: {
                            "Content-Type": "application/json"
                          }
                        }
                      )
                      if (res.status == 200) {
                        history.replace(Routes.LOGIN)
                      }
                    }}
                  >
                    {t("Sign in")}
                  </span>
                </p>
              </div>
            }
          />
        )
      case "password-reset":
        const queryParams = new URLSearchParams(window.location.search)
        const passwordResetCode = queryParams.get("passwordResetCode")

        return (
          <AuthForm
            type="reset"
            heading={
              isMobileLandscape ? null : (
                <Heading.Default title={t("Reset Password")} />
              )
            }
            logo={<Logo src={roomStyles?.authPageHeaderImage} noPad />}
            redirectToUrl={
              passwordResetCode ? (postLoginRedirectUrl as string) : undefined
            }
            successMessage={email => {
              return !passwordResetCode ? (
                <FormText
                  color={
                    roomStyles?.authPagePanelTextColor ?? "rgb(74, 85, 104)"
                  }
                  fontSize="13px"
                  lineHeight="1rem"
                  mt=".75rem"
                  textAlign="center"
                >
                  {`If ${email} is registered, a password reset email will be sent.`}
                </FormText>
              ) : null
            }}
            onSubmitRequest={({ email, passwordConfirm }) => {
              if (!meetingClient) throw "Meeting client not ready"
              return axios.post(
                meetingClient?.envConfig.USER_PASSWORD_RESET,
                {
                  email,
                  newPassword: passwordConfirm,
                  ...(passwordResetCode
                    ? { passwordResetCode: passwordResetCode }
                    : {}),
                  host: createHost(meetingClient?.envConfig.DOMAIN_HOSTNAME)
                },
                {
                  withCredentials: true,
                  headers: {
                    "Content-Type": "application/json"
                  }
                }
              )
            }}
            extraRequestBodyProps={{
              passwordResetCode
            }}
            emailInput={
              !passwordResetCode
                ? ({ errors, values, handleChange, handleBlur }) => (
                    <Input.Default
                      title={t("Email")}
                      type="email"
                      error={errors.email}
                      value={values.email}
                      id="email-input"
                      name="email"
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                      autoComplete="email"
                    />
                  )
                : undefined
            }
            {...(!passwordResetCode
              ? {}
              : {
                  passwordInput: ({
                    errors,
                    values,
                    handleChange,
                    handleBlur
                  }) => (
                    <Input.Default
                      title={t("Password")}
                      type="password"
                      error={errors.password}
                      value={values.password}
                      id="password-input"
                      name="password"
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                      autoComplete="new-password"
                    />
                  ),
                  passwordConfirmInput: ({
                    errors,
                    values,
                    handleChange,
                    handleBlur
                  }) => (
                    <Input.Default
                      title={t("Confirm Password")}
                      type="password"
                      error={errors.passwordConfirm}
                      value={values.passwordConfirm}
                      id="passwordConfirm-input"
                      name="passwordConfirm"
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                      autoComplete="new-password"
                    />
                  )
                })}
            submitButton={({ isSubmitting }) => (
              <Submit.Default title={t("Reset")} isSubmitting={isSubmitting} />
            )}
            navigationPanel={
              <div
                style={{
                  display: "flex",
                  justifyContent: isMobileLandscape ? "space-evenly" : "center",
                  flexDirection: isMobileLandscape ? "row" : "column"
                }}
              >
                <p
                  style={{
                    textAlign: "center",
                    color: roomStyles?.authPagePanelSubTextColor
                      ? roomStyles?.authPagePanelSubTextColor
                      : "#4A5568"
                  }}
                >
                  {t("Already got an account?")}{" "}
                  <Link
                    to={addCurrentQueryParams("/user-login")}
                    style={{ textDecoration: "underline" }}
                  >
                    {t("Sign in")}
                  </Link>
                </p>
              </div>
            }
          />
        )

      default:
        return <>Unhandled form type</>
    }
  }

  if (learnmachineAuthPage === "loading") return null

  return form(learnmachineAuthPage as boolean)
}

export default Authentication
